import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Wifi, Copy, Eye, Crown, ArrowLeft, Zap, Check, Radio, Gem,
} from "lucide-react";
import LocationPicker from "../components/LocationPicker";
import UserLink from "../components/UserLink";
import { T } from "../theme";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMOJI_OPTIONS = ["✨", "✌️", "🤝", "💖", "🙏", "🎉", "🌈", "⚡", "🔥", "💫"];

const COLOR_OPTIONS = [
  "#FF1493", "#FF3CAC", "#FF006E", "#FF4500",
  "#FFA500", "#FFD700", "#00FF00", "#00CED1",
  "#00F5FF", "#54A0FF", "#9D4EDD", "#8A2BE2",
];

// ─── Design tokens (shared with future dashboard/collection) ──────────────────


const cardStyle = {
  width: "100%",
  background: T.cardBg,
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  border: T.cardBorder,
  borderRadius: T.cardRadius,
  padding: T.cardPad,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

const inputStyle = {
  width: "100%",
  padding: "13px 18px",
  background: T.inputBg,
  border: T.inputBorder,
  borderRadius: 12,
  color: T.textPrimary,
  fontSize: "1rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const btnPrimary = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
  padding: "13px 20px",
  background: "linear-gradient(135deg, #ff3cac, #b44fff)",
  border: "none", borderRadius: 12, color: "#fff",
  fontSize: "0.95rem", fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
  transition: "opacity 0.15s, transform 0.15s",
};

const btnGhost = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "11px 18px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12, color: "rgba(255,255,255,0.7)",
  fontSize: "0.9rem", fontWeight: 600,
  cursor: "pointer",
  transition: "background 0.15s",
};

const btnCyan = {
  ...btnPrimary,
  background: "linear-gradient(135deg, #00f5ff, #b44fff)",
  boxShadow: "0 4px 20px rgba(0,245,255,0.25)",
};

// ─── Small display helpers ────────────────────────────────────────────────────

function NfcHero() {
  return (
    <div style={{ position: "relative", width: 130, height: 130, margin: "0 auto 28px" }}>
      {[0, 0.7, 1.4].map((delay, i) => (
        <div
          key={i}
          className="animate-scan-ring"
          style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            border: "2px solid rgba(0,245,255,0.45)",
            animationDelay: `${delay}s`,
          }}
        />
      ))}
      <div style={{
        position: "absolute", inset: "18%",
        background: "radial-gradient(circle, rgba(0,245,255,0.15) 0%, transparent 70%)",
        borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Wifi size={44} color={T.accentCyan} style={{ filter: `drop-shadow(0 0 14px ${T.accentCyan})` }} />
      </div>
    </div>
  );
}

function KandiBead({ color, size = 42, removable, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      title={removable ? "Click to remove" : color}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, ${color} 55%)`,
        border: "2px solid rgba(255,255,255,0.28)",
        boxShadow: `0 0 10px ${color}88, 0 2px 6px rgba(0,0,0,0.4)`,
        cursor: removable ? "pointer" : "default",
        transform: hovered && removable ? "scale(1.15)" : "scale(1)",
        transition: "transform 0.15s",
      }}
    />
  );
}

function AccessBadge({ hasAccess }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 18px", borderRadius: 50, marginBottom: 24,
      fontSize: "0.85rem", fontWeight: 600,
      ...(hasAccess ? {
        background: "linear-gradient(135deg, rgba(0,245,255,0.12), rgba(180,79,255,0.12))",
        border: "1px solid rgba(0,245,255,0.35)",
        color: T.accentCyan,
      } : {
        background: "rgba(255,152,0,0.12)",
        border: "1px solid rgba(255,152,0,0.35)",
        color: "#ffb347",
      }),
    }}>
      {hasAccess ? <Check size={14} /> : <Eye size={14} />}
      {hasAccess ? "Verified — You have physical possession" : "View Only — Tap NFC tag to interact"}
    </div>
  );
}

function StatBubble({ value, label, color }) {
  return (
    <div style={{
      flex: 1, textAlign: "center", padding: "16px 12px",
      background: "rgba(255,255,255,0.04)",
      borderRadius: 16, border: `1px solid ${color}30`,
    }}>
      <div style={{
        fontSize: "clamp(1.6rem, 4vw, 2.2rem)", fontWeight: 800,
        color, lineHeight: 1, marginBottom: 5,
        textShadow: `0 0 20px ${color}80`,
      }}>
        {value}
      </div>
      <div style={{
        fontSize: "0.75rem", color: T.textMuted,
        textTransform: "uppercase", letterSpacing: "0.1em",
      }}>
        {label}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: "0.75rem", fontWeight: 700,
      color: T.textMuted,
      textTransform: "uppercase", letterSpacing: "0.1em",
      marginBottom: 8, marginTop: 0,
    }}>
      {children}
    </p>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      marginTop: 16, padding: "12px 16px",
      background: "rgba(255,60,60,0.1)",
      border: "1px solid rgba(255,60,60,0.3)",
      borderRadius: 10, color: "#ff8080",
      fontSize: "0.9rem",
    }}>
      {msg}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ScanKandiPage() {
  const [mode, setMode]                       = useState("scan");
  const [kandiId, setKandiId]                 = useState("");
  const [uid, setUid]                         = useState(null);
  const [hasPhysicalAccess, setHasPhysicalAccess] = useState(false);
  const [kandiData, setKandiData]             = useState(null);
  const [formData, setFormData]               = useState({ name: "", emoji: "✨", colors: [] });
  const [locationData, setLocationData]       = useState({ location: "", lat: null, lng: null, eventName: null });
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState("");
  const [copied, setCopied]                   = useState(null); // "full" | "view"

  const navigate   = useNavigate();
  const location   = useLocation();
  const API_URL    = process.env.REACT_APP_API_URL || "";
  const user       = JSON.parse(localStorage.getItem("user") || "{}");

  // ── Check URL for UID on mount ───────────────────────────────────────────
  useEffect(() => {
    const urlParams   = new URLSearchParams(location.search);
    const uidFromUrl  = urlParams.get("uid");

    if (uidFromUrl) {
      setUid(uidFromUrl);
      const pathParts  = location.pathname.split("/");
      const idFromPath = pathParts[pathParts.length - 1];

      if (idFromPath && idFromPath.startsWith("KK-")) {
        setKandiId(idFromPath);
        verifyAndLoadKandi(idFromPath, uidFromUrl);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ── Verify UID and load kandi ────────────────────────────────────────────
  const verifyAndLoadKandi = async (kandiId, scannedUid) => {
    setLoading(true);
    setError("");
    try {
      const verifyResponse = await axios.post(
        `${API_URL}/api/kandi/${kandiId}/verify`,
        { uid: scannedUid }
      );
      if (verifyResponse.data.verified) {
        setUid(scannedUid);
        setHasPhysicalAccess(true);
        const kandiResponse = await axios.get(`${API_URL}/api/kandi/${kandiId}`);
        setKandiData(kandiResponse.data);
        setMode("view");
      } else {
        setError("Invalid UID — This kandi does not match the NFC tag");
        setHasPhysicalAccess(false);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setUid(scannedUid);
        setHasPhysicalAccess(true);
        setMode("register");
      } else if (err.response?.status === 403) {
        setError("Invalid UID — You must have the physical kandi to interact with it");
        setHasPhysicalAccess(false);
      } else {
        setError(err.response?.data?.error || "Failed to verify kandi");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Manual scan (view-only) ──────────────────────────────────────────────
  const handleManualScan = async () => {
    if (!kandiId.trim()) { setError("Please enter a Kandi ID"); return; }
    setLoading(true);
    setError("");
    setHasPhysicalAccess(false);
    try {
      const response = await axios.get(`${API_URL}/api/kandi/${kandiId}`);
      setKandiData(response.data);
      setMode("view");
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Kandi not found. To register a new kandi, scan the NFC tag.");
      } else {
        setError(err.response?.data?.error || "Failed to check kandi");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Register new kandi ───────────────────────────────────────────────────
  const handleRegister = async () => {
    if (!uid) {
      setError("Cannot register without UID. Please scan the NFC tag with the full URL.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_URL}/api/kandi/create`, {
        userId:   user.id,
        username: user.username,
        name:     formData.name || "Untitled Kandi",
        emoji:    formData.emoji,
        colors:   formData.colors,
        location: locationData.location || "Registered via app",
        lat:      locationData.lat,
        lng:      locationData.lng,
      });
      if (response.data.success) {
        const createdId  = response.data.kandi.id;
        const createdUid = response.data.kandi.uid;
        const kandiResponse = await axios.get(`${API_URL}/api/kandi/${createdId}`);
        setKandiData(kandiResponse.data);
        setKandiId(createdId);
        setUid(createdUid);
        setHasPhysicalAccess(true);
        setMode("view");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to register kandi");
    } finally {
      setLoading(false);
    }
  };

  // ── Claim kandi ──────────────────────────────────────────────────────────
  const handleClaim = async () => {
    if (!uid || !hasPhysicalAccess) {
      setError("You must scan the NFC tag with the full URL to claim this kandi");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_URL}/api/kandi/${kandiId}/claim`, {
        userId: user.id, username: user.username, uid,
        location: locationData.location || "Claimed via app",
        lat: locationData.lat, lng: locationData.lng,
      });
      if (response.data.success) {
        const updated = await axios.get(`${API_URL}/api/kandi/${kandiId}`);
        setKandiData(updated.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to claim kandi");
    } finally {
      setLoading(false);
    }
  };

  // ── Log scan ─────────────────────────────────────────────────────────────
  const handleLogScan = async () => {
    if (!uid || !hasPhysicalAccess) {
      setError("You must scan the NFC tag with the full URL to log a scan");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_URL}/api/kandi/${kandiId}/scan`, {
        userId: user.id, username: user.username, uid,
        location: locationData.location || "Scanned via app",
        lat: locationData.lat, lng: locationData.lng,
        message: "Scanned this kandi",
      });
      if (response.data.success) {
        const updated = await axios.get(`${API_URL}/api/kandi/${kandiId}`);
        setKandiData(updated.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to log scan");
    } finally {
      setLoading(false);
    }
  };

  // ── Copy URL helpers ─────────────────────────────────────────────────────
  const handleCopyUrl = () => {
    if (!kandiId || !uid) return;
    const url = `${window.location.origin}/kandi/${kandiId}?uid=${uid}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied("full");
      setTimeout(() => setCopied(null), 2200);
    });
  };

  const handleCopyViewOnlyUrl = () => {
    if (!kandiId) return;
    const url = `${window.location.origin}/kandi/${kandiId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied("view");
      setTimeout(() => setCopied(null), 2200);
    });
  };

  // ── Form helpers ─────────────────────────────────────────────────────────
  const addColorDirect = (color) => {
    if (formData.colors.length < 6) {
      setFormData((prev) => ({ ...prev, colors: [...prev.colors, color] }));
    }
  };

  const removeColor = (index) => {
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index),
    }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="scan-root" style={{
      minHeight: "100vh",
      background: T.bg,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "88px 16px 48px",
    }}>

      {/* ── SCAN MODE ──────────────────────────────────────────────────── */}
      {mode === "scan" && (
        <div className="scan-glass-card" style={{ ...cardStyle, maxWidth: 460 }}>
          <NfcHero />

          <h1 style={{
            textAlign: "center", margin: "0 0 10px",
            fontFamily: "var(--font-display)",
            fontSize: "clamp(1.6rem, 4vw, 2rem)", fontWeight: 700,
            color: T.accentCyan,
            textShadow: `0 0 24px ${T.accentPurple}60`,
          }}>
            Tap to Scan
          </h1>

          <p style={{ textAlign: "center", color: T.textMuted, margin: "0 0 8px", fontSize: "0.95rem" }}>
            Hold your kandi's NFC tag near your phone
          </p>
          <p style={{ textAlign: "center", color: T.textMuted, margin: "0 0 28px", fontSize: "0.85rem" }}>
            iPhone &amp; Android — your phone opens the full URL automatically
          </p>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 24px" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
            <span style={{ color: T.textMuted, fontSize: "0.8rem", letterSpacing: "0.06em" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
          </div>

          <SectionLabel>Enter Kandi ID (view only)</SectionLabel>
          <input
            type="text"
            placeholder="KK-000001"
            value={kandiId}
            onChange={(e) => setKandiId(e.target.value.toUpperCase())}
            style={inputStyle}
            onKeyDown={(e) => e.key === "Enter" && handleManualScan()}
          />
          <p style={{ fontSize: "0.8rem", color: T.textMuted, margin: "8px 0 18px" }}>
            Manual entry is view-only. Tap the NFC tag to claim or log scans.
          </p>

          <button
            onClick={handleManualScan}
            disabled={loading}
            style={{ ...btnPrimary, width: "100%", opacity: loading ? 0.6 : 1 }}
          >
            <Eye size={17} />
            {loading ? "Checking…" : "View Kandi"}
          </button>

          <ErrorMsg msg={error} />
        </div>
      )}

      {/* ── REGISTER MODE ──────────────────────────────────────────────── */}
      {mode === "register" && (
        <div className="scan-glass-card" style={{ ...cardStyle, maxWidth: 500 }}>
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "7px 16px", borderRadius: 50, marginBottom: 20,
            background: "linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,60,172,0.18))",
            border: "1px solid rgba(255,215,0,0.35)", color: T.accentGold,
            fontSize: "0.85rem", fontWeight: 700,
          }}>
            <Radio size={14} />
            New Kandi Found!
          </div>

          <h1 style={{
            margin: "0 0 6px",
            fontFamily: "var(--font-display)",
            fontSize: "clamp(1.5rem, 4vw, 1.9rem)", fontWeight: 700,
            color: T.accentGold,
            textShadow: `0 0 22px ${T.accentPink}55`,
          }}>
            Register This Kandi
          </h1>
          <p style={{ color: T.textMuted, margin: "0 0 28px", fontSize: "0.9rem" }}>
            Give it a name and some personality
          </p>

          {/* Name */}
          <div style={{ marginBottom: 22 }}>
            <SectionLabel>Kandi Name</SectionLabel>
            <input
              type="text"
              placeholder="Peace Bracelet"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={inputStyle}
            />
          </div>

          {/* Emoji */}
          <div style={{ marginBottom: 22 }}>
            <SectionLabel>Choose Emoji</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => setFormData({ ...formData, emoji })}
                  className="emoji-btn"
                  style={{
                    padding: "10px 6px", borderRadius: 10,
                    fontSize: "1.4rem", lineHeight: 1,
                    minHeight: 52,
                    background: formData.emoji === emoji
                      ? "linear-gradient(135deg, rgba(180,79,255,0.35), rgba(255,60,172,0.35))"
                      : "rgba(255,255,255,0.05)",
                    border: formData.emoji === emoji
                      ? "1px solid rgba(180,79,255,0.6)"
                      : "1px solid rgba(255,255,255,0.08)",
                    cursor: "pointer", transition: "background 0.15s, border 0.15s",
                    touchAction: "manipulation",
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Color picker */}
          <div style={{ marginBottom: 22 }}>
            <SectionLabel>
              Bead Colors — {formData.colors.length}/6 selected
            </SectionLabel>
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(6, 1fr)",
              gap: 10, marginBottom: 14,
            }}>
              {COLOR_OPTIONS.map((color) => (
                <div
                  key={color}
                  onClick={() => addColorDirect(color)}
                  title={color}
                  style={{
                    width: "100%", aspectRatio: "1",
                    borderRadius: "50%",
                    background: `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, ${color} 55%)`,
                    border: "2px solid rgba(255,255,255,0.2)",
                    boxShadow: `0 0 8px ${color}66`,
                    cursor: formData.colors.length < 6 ? "pointer" : "not-allowed",
                    opacity: formData.colors.length >= 6 ? 0.45 : 1,
                    transition: "transform 0.15s",
                  }}
                  onMouseEnter={(e) => { if (formData.colors.length < 6) e.currentTarget.style.transform = "scale(1.2)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                />
              ))}
            </div>

            {/* Color preview row */}
            <div style={{
              display: "flex", gap: 8, minHeight: 44,
              padding: "10px 14px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.07)",
              flexWrap: "wrap",
              alignItems: "center",
            }}>
              {formData.colors.length === 0 ? (
                <span style={{ color: T.textMuted, fontSize: "0.85rem" }}>
                  Tap colors above to add beads
                </span>
              ) : (
                formData.colors.map((color, i) => (
                  <KandiBead key={i} color={color} size={36} removable onClick={() => removeColor(i)} />
                ))
              )}
            </div>
            {formData.colors.length > 0 && (
              <p style={{ fontSize: "0.75rem", color: T.textMuted, margin: "6px 0 0" }}>
                Tap a bead to remove it
              </p>
            )}
          </div>

          {/* Location */}
          <LocationPicker value={locationData} onChange={setLocationData} />

          {/* Buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => setMode("scan")} style={{ ...btnGhost, flex: 1 }}>
              <ArrowLeft size={15} /> Back
            </button>
            <button
              onClick={handleRegister}
              disabled={loading}
              style={{ ...btnPrimary, flex: 2, opacity: loading ? 0.6 : 1 }}
            >
              <Gem size={16} />
              {loading ? "Registering…" : "Register Kandi"}
            </button>
          </div>

          <ErrorMsg msg={error} />
        </div>
      )}

      {/* ── VIEW MODE ──────────────────────────────────────────────────── */}
      {mode === "view" && kandiData && (
        <div className="scan-glass-card" style={{ ...cardStyle, maxWidth: 560 }}>
          <AccessBadge hasAccess={hasPhysicalAccess} />

          {/* Kandi header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{
              fontSize: "clamp(3.5rem, 10vw, 5rem)", lineHeight: 1, marginBottom: 14,
              filter: "drop-shadow(0 0 20px rgba(255,255,255,0.2))",
            }}>
              {kandiData.emoji}
            </div>
            <h1 style={{
              margin: "0 0 6px",
              fontFamily: "var(--font-display)",
              fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 700,
              color: T.accentPink,
              textShadow: `0 0 24px ${T.accentPurple}60`,
            }}>
              {kandiData.name}
            </h1>
            <p style={{
              color: T.textMuted, fontSize: "0.82rem",
              fontFamily: "monospace", margin: 0, letterSpacing: "0.05em",
            }}>
              {kandiData.id}
            </p>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
            <StatBubble value={kandiData.scanCount ?? 0}       label="Scans"  color={T.accentCyan}   />
            <StatBubble value={kandiData.history?.length ?? 0} label="Owners" color={T.accentPurple} />
          </div>

          {/* Bead colors */}
          {kandiData.colors?.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <SectionLabel>Bead Colors</SectionLabel>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {kandiData.colors.map((color, i) => (
                  <KandiBead key={i} color={color} size={48} />
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "0 0 20px" }} />

          {/* Ownership journey */}
          <div style={{ marginBottom: 24 }}>
            <SectionLabel>Journey</SectionLabel>
            {kandiData.history?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {kandiData.history.map((entry, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    padding: "12px 0",
                    borderBottom: i < kandiData.history.length - 1
                      ? "1px solid rgba(255,255,255,0.05)" : "none",
                  }}>
                    {/* Step indicator */}
                    <div style={{
                      width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: i === 0
                        ? `linear-gradient(135deg, ${T.accentGold}, ${T.accentPink})`
                        : "rgba(255,255,255,0.07)",
                      border: i === 0
                        ? "none"
                        : "1px solid rgba(255,255,255,0.1)",
                    }}>
                      {i === 0
                        ? <Crown size={15} color="#fff" />
                        : <span style={{ fontSize: "0.75rem", color: T.textMuted, fontWeight: 700 }}>{i + 1}</span>
                      }
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: "0.95rem" }}>
                        <UserLink username={entry.username} userId={entry.userId} style={{ fontSize: "0.95rem" }} />
                      </p>
                      <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.8rem" }}>
                        {new Date(entry.date).toLocaleDateString("en-US", {
                          month: "short", day: "numeric", year: "numeric",
                        })}
                        {entry.location && ` · ${entry.location}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: T.textMuted, fontSize: "0.9rem", margin: 0 }}>
                No history yet — be the first owner!
              </p>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "0 0 20px" }} />

          {/* Location for scan/claim */}
          {hasPhysicalAccess && (
            <LocationPicker value={locationData} onChange={setLocationData} />
          )}

          {/* Actions */}
          {hasPhysicalAccess ? (
            <>
              <div className="scan-actions" style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <button
                  onClick={handleLogScan}
                  disabled={loading}
                  style={{ ...btnCyan, flex: 1, opacity: loading ? 0.6 : 1 }}
                >
                  <Zap size={16} />
                  {loading ? "Logging…" : "Log Scan"}
                </button>
                <button
                  onClick={handleClaim}
                  disabled={loading}
                  style={{ ...btnPrimary, flex: 1, opacity: loading ? 0.6 : 1 }}
                >
                  <Crown size={16} />
                  {loading ? "Claiming…" : "Claim Ownership"}
                </button>
              </div>

              {/* Share */}
              <div className="scan-share" style={{ marginBottom: 20 }}>
                <SectionLabel>Share This Kandi</SectionLabel>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={handleCopyUrl} style={{ ...btnGhost, flex: 1 }}>
                    {copied === "full" ? <Check size={14} color={T.accentCyan} /> : <Copy size={14} />}
                    {copied === "full" ? "Copied!" : "Full URL"}
                  </button>
                  <button onClick={handleCopyViewOnlyUrl} style={{ ...btnGhost, flex: 1 }}>
                    {copied === "view" ? <Check size={14} color={T.accentCyan} /> : <Eye size={14} />}
                    {copied === "view" ? "Copied!" : "View-Only"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{
              textAlign: "center", padding: "18px 20px", marginBottom: 20,
              background: "rgba(255,152,0,0.07)",
              borderRadius: 12, border: "1px solid rgba(255,152,0,0.2)",
            }}>
              <p style={{ color: "#ffb347", fontWeight: 600, margin: "0 0 6px", fontSize: "0.95rem" }}>
                Tap the NFC tag to interact
              </p>
              <p style={{ color: T.textMuted, fontSize: "0.85rem", margin: 0 }}>
                Your phone will open the full URL with claim &amp; scan access
              </p>
            </div>
          )}

          <ErrorMsg msg={error} />

          {/* Back link — hidden on mobile (bottom nav handles navigation) */}
          <button
            onClick={() => navigate("/dashboard")}
            className="scan-back-btn"
            style={{ ...btnGhost, width: "100%", marginTop: 8 }}
          >
            <ArrowLeft size={15} />
            Back to Dashboard
          </button>
        </div>
      )}
    </div>
  );
}

export default ScanKandiPage;
